import {
  OwnershipNode,
  OwnershipInterest,
  ConveyanceEdge,
  EventType,
  OwnershipGraph,
  InterestEncumbrance,
} from './types';
import { format0, formatNumber } from 'utils/number';

// ============================================================================
// Graph Helpers
// ============================================================================

// * Get all nodes as an array
// @param graph The ownership graph
// @returns Array of all ownership nodes
export const getNodes = (graph: OwnershipGraph): OwnershipNode[] =>
  Object.values(graph.nodes);

// * Get all edges as an array
// @param graph The ownership graph
// @returns Array of all conveyance edges
export const getEdges = (graph: OwnershipGraph): ConveyanceEdge[] =>
  Object.values(graph.edges);

// * Format a node volume depth range in the whitepaper's interval notation.
// This is the single canonical depth-range formatter for the Title Validator:
// every surface (runsheet rows, applicable-area chips, ownership table, graph
// nodes, severance visualization) renders depth through it so the notation
// stays consistent with the mathematical model (`D = [a, b] ⊆ [0, ∞)`).
//   [0, ∞)        full vertical extent (depthTo === null, depthFrom 0)
//   [9,500, ∞)    deep-complement band a grantor retains after a partial-depth
//                 conveyance — bounded below, unbounded above; keeps its lower
//                 bound (must NOT collapse to [0, ∞))
//   [0, 9,500]    shallow conveyed band
//   [5,000, 8,000] interior band
// @param depthRange The [from, to] tuple; `to === null` means unbounded above.
// @returns The bracket-interval label, e.g. "[9,500, ∞)".
export const formatDepthBand = ([depthFrom, depthTo]: [
  number,
  number | null,
]): string => {
  return depthTo !== null
    ? `[${format0(depthFrom ?? 0)}, ${format0(depthTo)}]`
    : `[${format0(depthFrom ?? 0)}, ∞)`;
};

// * Display a numeric fraction as an 8-decimal string (e.g. 0.33333333)
// @param fractionF The numeric fraction value
// @returns The fraction formatted to 8 decimal places
export const formatFraction = (fractionF: number): string => {
  return formatNumber(fractionF, 8, 8, 'decimal', false, '0.00000000');
};

// * Display an interest's fraction as a decimal string
// @param interest The ownership interest to format
// @returns The interest fraction formatted as a decimal string
export const formatInterestFraction = (interest: OwnershipInterest): string =>
  formatFraction(interest.fractionF);

// * Read the first non-null NRI value from a node's interests.
// Returns null if no interest has a computed NRI.
// @param node The ownership node to inspect
// @returns The first non-null NRI value, or null if none exists
export const getNodeNRI = (node: OwnershipNode): number | null => {
  for (const interest of node.interests) {
    if (interest.netF !== null) return interest.netF;
  }
  return null;
};

export function parseFractionString(s: string): number {
  const parts = s.split('/');
  if (parts.length === 2) {
    const num = parseFloat(parts[0]);
    const den = parseFloat(parts[1]);
    if (!isNaN(num) && !isNaN(den) && den !== 0) return num / den;
  }
  const direct = parseFloat(s);
  return isNaN(direct) ? 0 : direct;
}

// Longer denominators render as decimals.
const MAX_EXACT_DENOMINATOR_DIGITS = 4;

export function buildNriFormula(node: OwnershipNode): string | null {
  const fmt = (v: number) =>
    formatNumber(v, 8, 8, 'decimal', false, '0.00000000');
  /**
   * An exact fraction as sent, or as a decimal once its denominator is too long to read.
   * @param {number|string} v The fraction as the API sends it
   * @return {string} The fraction to show
   */
  const fmtExact = (v: number | string) => {
    const s = String(v);
    return (s.split('/')[1] ?? '').length > MAX_EXACT_DENOMINATOR_DIGITS
      ? fmt(parseFractionString(s))
      : s;
  };

  const workingInterest = node.interests.find(
    (i) => i.interestType === 'working' && i.fractionF > 0
  );
  if (workingInterest) {
    const gross = workingInterest.fractionF;
    const nri = workingInterest.netF;
    if (nri === null) return null;

    const orri = (workingInterest.encumbrances ?? [])
      .filter((e) => e.type === 'orri')
      .reduce((sum, e) => sum + parseFractionString(e.amount ?? '0'), 0);
    // A result stored before the rates were published shows the rate its net implies.
    const royalty = workingInterest.royaltyRates
      ? workingInterest.royaltyRates.reduce(
          (sum, l) => sum + parseFractionString(l.royaltyRate),
          0
        )
      : 1 - (nri + orri) / gross;

    return `${fmt(gross)} \u00d7 (1 \u2212 ${fmt(royalty)}) \u2212 ${fmt(
      orri
    )} = ${fmt(nri)}`;
  }

  const orriInterest = node.interests.find(
    (i) => i.interestType === 'orri' && i.fractionF > 0
  );
  if (orriInterest) {
    const gross = orriInterest.fractionF;
    const nri = orriInterest.netF;
    if (nri === null) return null;
    return `${fmt(gross)} = ${fmt(nri)}`;
  }

  const royaltyInterest = node.interests.find(
    (i) => i.interestType === 'royalty' && i.fractionF > 0
  );
  if (royaltyInterest) {
    const nri = royaltyInterest.netF;
    if (nri === null) return null;

    const leases = royaltyInterest.leaseShares ?? [];
    if (leases.length > 0) {
      const terms = leases.map((l) =>
        parseFractionString(l.share) === 1
          ? fmtExact(l.royaltyRate)
          : `${fmtExact(l.royaltyRate)} \u00d7 ${fmtExact(l.share)}`
      );
      const rate = terms.length === 1 ? terms[0] : `(${terms.join(' + ')})`;
      return `${fmtExact(royaltyInterest.fraction)} \u00d7 ${rate} = ${fmt(
        nri
      )}`;
    }

    const impliedRate =
      royaltyInterest.fractionF > 0 ? nri / royaltyInterest.fractionF : 0;
    return `${fmt(royaltyInterest.fractionF)} \u00d7 ${fmt(
      impliedRate
    )} = ${fmt(nri)}`;
  }

  return null;
}

export interface HeldEncumbrance {
  burdened_node: OwnershipNode;
  interest_type: string;
  enc: InterestEncumbrance;
}

export function buildHeldEncumbrances(
  nodeId: string,
  graph: OwnershipGraph
): HeldEncumbrance[] {
  const result: HeldEncumbrance[] = [];
  for (const [id, node] of Object.entries(graph.nodes)) {
    const isLeaf = !graph.adjacency[id] || graph.adjacency[id].length === 0;
    if (!isLeaf) continue;
    for (const interest of node.interests) {
      for (const enc of interest.encumbrances ?? []) {
        if (String(enc.encumbranceOwnerNodeId) === nodeId) {
          result.push({
            burdened_node: node,
            interest_type: interest.interestType,
            enc,
          });
        }
      }
    }
  }
  return result;
}

// ============================================================================
// Event Type Display Helpers
// ============================================================================

const EVENT_TYPE_NAMES: Record<EventType, string> = {
  E1: 'Estate Severance',
  E2: 'Property Severance',
  E3: 'Encumbrance Severance',
  E4: 'Interest Division',
  E5: 'Consolidation',
  E6: 'Encumbrance Pointer Update',
  E7: 'Lease Termination',
};

// GoJS renders on <canvas>, not DOM, so Carbon SCSS tokens can't be used.
// These hex values correspond to the Carbon v10 tokens noted in comments.
const EVENT_TYPE_COLORS: Record<EventType, string> = {
  E1: '#0043ce', // $blue-70
  E2: '#198038', // $green-60
  E3: '#ff832b', // $orange-40
  E4: '#1192e8', // $cyan-50
  E5: '#8a3ffc', // $purple-60
  E6: '#b28600', // $yellow-50
  E7: '#da1e28', // $red-60
};

// * Get the human-readable name for an event type (e.g. "Interest Division")
// @param eventType The event type code (E1-E7)
// @returns The human-readable event type name
export const getEdgeTypeName = (eventType: string): string =>
  EVENT_TYPE_NAMES[eventType as EventType] || eventType;

// * Get a display label for an edge operation.
// * For E4 (Interest Division), detects a Transfer when the conveyor's
// * interests fully match the conveyee's — per Remark 9.1 of the whitepaper.
// @param eventType The event type code (E1-E7)
// @param [isRetained] Whether to append "[Retained]" to the label
// @param [fromNode] The source (conveyor) node — used for E4 transfer detection
// @param [toNode] The destination (conveyee) node — used for E4 transfer detection
// @returns The display label
export const getEdgeTypeLabel = (
  eventType: string,
  isRetained?: boolean,
  fromNode?: {
    interests: Array<{ interestType: string; fractionF: number }>;
  } | null,
  toNode?: {
    interests: Array<{ interestType: string; fractionF: number }>;
  } | null
): string => {
  let base = EVENT_TYPE_NAMES[eventType as EventType] || eventType;
  // E4: if the conveyee received the same interests as the conveyor held, it's a Transfer
  if (eventType === 'E4' && !isRetained && fromNode && toNode) {
    const fromInterests = fromNode.interests
      .filter((i) => i.fractionF > 0)
      .sort((a, b) => a.interestType.localeCompare(b.interestType));
    const toInterests = toNode.interests
      .filter((i) => i.fractionF > 0)
      .sort((a, b) => a.interestType.localeCompare(b.interestType));
    if (
      fromInterests.length > 0 &&
      fromInterests.length === toInterests.length &&
      fromInterests.every(
        (fi, idx) =>
          fi.interestType === toInterests[idx].interestType &&
          Math.abs(fi.fractionF - toInterests[idx].fractionF) < 0.000001
      )
    ) {
      base = 'Transfer';
    }
  }
  return isRetained ? `${base} [Retained]` : base;
};

export const getEdgeTypeColor = (eventType: string): string =>
  EVENT_TYPE_COLORS[eventType as EventType] || '#525252';

// ============================================================================
// Interest Labels — single source of truth
// ============================================================================

const INTEREST_LABELS: Record<string, string> = {
  mineral: 'Mineral',
  surface: 'Surface',
  executive: 'Exec',
  royalty: 'Royalty',
  bonus: 'Bonus',
  delay: 'Delay',
  working: 'WI',
  orri: 'ORRI',
};

// * Format a snake_case string as Title Case (e.g. "lease_expired" -> "Lease Expired")
// @param s The snake_case string to convert
// @returns The string formatted in Title Case
export function snakeCaseToTitle(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getInterestLabel(interestType: string): string {
  return INTEREST_LABELS[interestType] ?? snakeCaseToTitle(interestType);
}

// ============================================================================
// Operation Expression — human-readable description of what was conveyed
// ============================================================================

export function formatInterestList(node: OwnershipNode): string {
  return node.interests
    .filter((i) => i.fractionF > 0)
    .map((i) => `${getInterestLabel(i.interestType)} ${i.fraction}`)
    .join(', ');
}

export function computeOperationExpression(
  edge: ConveyanceEdge,
  nodeMap: Record<string, OwnershipNode>
): string | null {
  const toNode = edge.toNodeId !== null ? nodeMap[edge.toNodeId] : undefined;
  const fromNode =
    edge.fromNodeId !== null ? nodeMap[edge.fromNodeId] : undefined;

  if (!toNode && !fromNode) return null;

  switch (edge.eventType) {
    case 'E1': {
      if (!toNode) return null;
      return formatInterestList(toNode) || null;
    }
    case 'E2': {
      const subtype = edge.params.severanceSubtype;
      const label =
        subtype === 'geospatial'
          ? 'Geospatial split'
          : subtype === 'depth'
            ? 'Depth split'
            : subtype
              ? snakeCaseToTitle(subtype)
              : 'Property split';
      if (toNode) {
        return `${label} \u2192 ${toNode.property.propertyName}`;
      }
      return label;
    }
    case 'E3': {
      if (!toNode) return null;
      const interests = formatInterestList(toNode);
      const subtype = edge.params.severanceSubtype;
      if (subtype === 'mineral_estate_split') {
        return interests
          ? `Estate split: ${interests}`
          : 'Mineral estate split';
      }
      return interests || null;
    }
    case 'E4': {
      if (!toNode) return null;
      return formatInterestList(toNode) || null;
    }
    case 'E5':
      return null;
    case 'E6':
      return 'Update encumbrance references';
    case 'E7': {
      const reason = edge.params.terminationReason;
      return reason ? snakeCaseToTitle(reason) : 'Lease expired';
    }
    default:
      return null;
  }
}

// * Build interest summary for graph node cards.
// Labels are padded to the longest label among the *visible* interests so
// the pipe separator aligns vertically -- rendered in monospace font.
// @param interests The array of ownership interests to summarize
// @returns Formatted multi-line summary string, or an em-dash if no interests
export const buildInterestSummary = (
  interests: OwnershipInterest[]
): string => {
  const entries = interests
    .filter((i) => i.fractionF > 0)
    .map((i) => ({
      label: getInterestLabel(i.interestType),
      value: formatFraction(i.fractionF),
    }));
  if (entries.length === 0) return '\u2014';
  const maxLen = Math.max(...entries.map((e) => e.label.length));
  const parts = entries.map(
    (e) => `${e.label}:${' '.repeat(maxLen - e.label.length + 1)}${e.value}`
  );
  const lines: string[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    lines.push(
      i + 1 < parts.length ? `${parts[i]} | ${parts[i + 1]}` : parts[i]
    );
  }
  return lines.join('\n');
};
